import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import TableCard from "../../../components/ui-components/TableCard";
import FlexTable from "../../../components/ui-components/FlexTable";
import EmptyState from "../../../components/ui-components/EmptyState";
import Badge from "../../../components/ui-components/Badge";
import { toast } from "../../../components/ui-components/Toast.jsx";
import { secondaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import { downloadRunArtifact } from "../document/boardReportApi.js";
import { formatPeriodLabel } from "../document/formatPeriod.js";

const RUN_STATUS_LABEL = {
  draft: "Draft",
  in_review: "In review",
  approved: "Approved",
  sent: "Sent",
  archived: "Archived",
};
const RUN_STATUS_TONE_KEY = {
  draft: "pending",
  in_review: "awaiting",
  approved: "approved",
  sent: "info",
  archived: "neutral",
};

function formatTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function approverLabel(run) {
  if (run.approved_by_name) return run.approved_by_name;
  if (run.approved_by != null) return `User #${run.approved_by}`;
  return "—";
}

// RunActionsMenu - a compact kebab tucking the audit-only PDF affordances (open the rendered PDF,
// download the stored file) away, so each row shows just "Preview" + "Review" by default rather than
// four near-identical view actions. Only rendered when the run has a persisted artifact.
const RunActionsMenu = ({ onOpenPdf, onDownload }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (fn) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div className="relative inline-flex" ref={ref}>
      <div
        role="button"
        tabIndex={0}
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="inline-flex items-center justify-center h-[28px] w-[28px] rounded-ctrl text-text-3 hover:bg-surface-2 hover:text-text cursor-pointer transition-colors"
      >
        <span aria-hidden="true" className="text-base leading-none">⋯</span>
      </div>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-[160px] rounded-card border border-card-bd bg-card p-1 shadow-lg"
        >
          <div
            role="menuitem"
            tabIndex={0}
            onClick={pick(onOpenPdf)}
            onKeyDown={(e) => (e.key === "Enter" ? pick(onOpenPdf)() : null)}
            className="block box-border rounded-ctrl px-3 py-2 text-left text-[13px] text-text-2 hover:bg-surface-2 hover:text-text cursor-pointer"
          >
            Open PDF
          </div>
          <div
            role="menuitem"
            tabIndex={0}
            onClick={pick(onDownload)}
            onKeyDown={(e) => (e.key === "Enter" ? pick(onDownload)() : null)}
            className="block box-border rounded-ctrl px-3 py-2 text-left text-[13px] text-text-2 hover:bg-surface-2 hover:text-text cursor-pointer"
          >
            Download PDF
          </div>
        </div>
      )}
    </div>
  );
};

RunActionsMenu.propTypes = {
  onOpenPdf: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

/**
 * RunsPanel - the Runs tab of the report home: the version history. The primary "New run" action
 * lives in the page header (BoardReportHome); this panel still offers it from the empty state when
 * there are no runs yet. Each row's default action is Review (click the row or the button); the
 * audit-only PDF actions collapse into a per-row kebab. Presentational: the home owns the run data.
 */
const RunsPanel = ({ id, reportName, runs, isLoading, error, reload, onNewRun }) => {
  const navigate = useNavigate();

  const openReview = useCallback(
    (runId) => navigate(`/board-reports/${id}/runs/${runId}/review`),
    [id, navigate]
  );

  const handleOpenPdf = useCallback(
    (run) => window.open(`/board-report-print/${id}/${run.id}?print=true`, "_blank", "noopener,noreferrer"),
    [id]
  );

  const handleDownload = useCallback(
    async (run) => {
      try {
        const { blob } = await downloadRunArtifact(id, run.id);
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `board-report-${id}-v${run.version}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        toast.error("Failed to download PDF");
      }
    },
    [id]
  );

  const columnDefs = useMemo(
    () => [
      {
        accessor: "version",
        Header: () => <span>Version</span>,
        Cell: ({ value }) => <span className="font-medium text-text">v{value}</span>,
        width: 90,
        minWidth: 70,
        maxWidth: 110,
      },
      {
        accessor: "period",
        Header: () => <span>Period</span>,
        Cell: ({ value }) => <span className="text-sm text-text-2">{formatPeriodLabel(value)}</span>,
        width: 150,
        minWidth: 120,
        maxWidth: 190,
      },
      {
        accessor: "status",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => (
          <Badge status={RUN_STATUS_TONE_KEY[value] || value} label={RUN_STATUS_LABEL[value] || value} />
        ),
        width: 130,
        minWidth: 110,
        maxWidth: 160,
      },
      {
        accessor: "approved_by",
        Header: () => <span>Approved by</span>,
        Cell: ({ row }) => <span className="text-sm text-text-2">{approverLabel(row.original)}</span>,
        width: 140,
        minWidth: 110,
        maxWidth: 180,
      },
      {
        accessor: "created_at",
        Header: () => <span>Created</span>,
        Cell: ({ value }) => <span className="text-sm text-text-2">{formatTimestamp(value)}</span>,
        width: 190,
        minWidth: 150,
        maxWidth: 220,
      },
      {
        id: "actions",
        align: "right",
        Header: () => null,
        Cell: ({ row }) => {
          const run = row.original;
          return (
            <div className="flex items-center justify-end gap-3" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                title="Open the interactive on-screen preview"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/board-reports/${id}/runs/${run.id}/preview`);
                }}
              >
                Preview
              </button>
              <button
                type="button"
                className={secondaryButtonCls}
                onClick={(e) => {
                  e.stopPropagation();
                  openReview(run.id);
                }}
              >
                Review
              </button>
              {run.artifact_id && (
                <RunActionsMenu onOpenPdf={() => handleOpenPdf(run)} onDownload={() => handleDownload(run)} />
              )}
            </div>
          );
        },
        width: 260,
        minWidth: 220,
      },
    ],
    [handleDownload, handleOpenPdf, navigate, openReview, id]
  );

  const list = Array.isArray(runs) ? runs : [];
  const matchCount = list.length;
  const noun = matchCount === 1 ? "run" : "runs";
  const hint = isLoading ? "Loading…" : `${matchCount} ${noun}`;

  return (
    <Fragment>
      <p className="mb-4 text-sm text-text-2">
        Every generated version of{" "}
        {reportName ? <span className="font-medium text-text">{reportName}</span> : "this report"}. Reviewing a run
        edits its content and approves it.
      </p>

      <TableCard
        title="Runs"
        subtitle={hint}
        isLoading={isLoading}
        error={error}
        errorMessage="Error loading runs"
        onRetry={reload}
      >
        {list.length === 0 ? (
          <EmptyState
            icon={icon({ name: "clock-rotate-left", style: "light" })}
            title="No runs yet"
            message="Generate the first run to produce a board-ready draft you can review and approve."
            action={{ label: "New run", onClick: onNewRun, icon: icon({ name: "plus", style: "regular" }) }}
          />
        ) : (
          <FlexTable
            columns={columnDefs}
            data={list}
            getRowId={(r) => r.id}
            onRowClick={(rid) => openReview(rid)}
            rowAriaLabel={(r) => `Review run v${r.version}`}
          />
        )}
      </TableCard>
    </Fragment>
  );
};

RunsPanel.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  reportName: PropTypes.string,
  runs: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  reload: PropTypes.func,
  onNewRun: PropTypes.func.isRequired,
};

export default RunsPanel;
